import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    CircularProgress,
    Container,
    Drawer,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Modal,
    Pagination,
    Paper,
    Select,
    TextField,
    Typography,
    useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import React, { useEffect, useMemo, useState } from "react";
import GameCodesForm from "./GameCodesForm";
import GameCodesTable from "./GameCodesTable";
import { getAllIncomeCollection } from "../../../data/api/incomes/IncomeRoutes";
import useIndex from "../../../hooks/useIndex";
import moneyFormat from "../../../hooks/moneyFormat";
import validErrors from "../../../hooks/validErrors";
import LeftPlaysPanel from "./LeftPlayPanel";
import styled from "styled-components";

const MONTHS = [
    { value: 1, label: "Enero" },
    { value: 2, label: "Febrero" },
    { value: 3, label: "Marzo" },
    { value: 4, label: "Abril" },
    { value: 5, label: "Mayo" },
    { value: 6, label: "Junio" },
    { value: 7, label: "Julio" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Septiembre" },
    { value: 10, label: "Octubre" },
    { value: 11, label: "Noviembre" },
    { value: 12, label: "Diciembre" },
];

const fieldStyles = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "14px",
        background: "#fff",
        color: "#10285f",
        fontWeight: 700,
        transition: "0.25s ease",
        "& fieldset": {
            borderColor: "rgba(15, 34, 77, 0.16)",
        },
        "&:hover fieldset": {
            borderColor: "rgba(214, 170, 58, 0.7)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#d6aa3a",
            borderWidth: "2px",
        },
    },
    "& .MuiInputLabel-root": {
        color: "rgba(15, 34, 77, 0.65)",
        fontWeight: 700,
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: "#10285f",
    },
    "& .MuiSvgIcon-root": {
        color: "#10285f",
    },
};

const buttonStyles = {
    borderRadius: "14px",
    minHeight: 42,
    fontWeight: 900,
    textTransform: "uppercase",
};

const GameCodes = () => {
    const { auth, token } = useIndex();

    const isMobile = useMediaQuery("(max-width:900px)");

    const [expanded, setExpanded] = useState(false);
    const [collection, setCollection] = useState(false);
    const [addGameCodes, setAddGameCodes] = useState(false);
    const [myCollections, setMyCollections] = useState([]);
    const [loadingCollections, setLoadingCollections] = useState(true);
    const [monthsPanelOpen, setMonthsPanelOpen] = useState(false);

    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
    const [filterDay, setFilterDay] = useState("");
    const [searchBy, setSearchBy] = useState("plays");
    const [searchText, setSearchText] = useState("");

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [meta, setMeta] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
    });

    const handleClose = () => {
        setAddGameCodes(false);
    };

    const years = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const arr = [];
        for (let y = 2025; y <= currentYear; y++) arr.push(y);
        return arr;
    }, []);

    const daysInMonth = useMemo(() => {
        if (!filterYear || !filterMonth) return [];
        const lastDay = new Date(filterYear, filterMonth, 0).getDate();
        return Array.from({ length: lastDay }, (_, i) => i + 1);
    }, [filterYear, filterMonth]);

    useEffect(() => {
        loadData(1, perPage);
    }, [filterMonth]);

    useEffect(() => {
        handleClose();
    }, [myCollections]);

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
        setCollection(panel);
    };

    const handleSelectMonth = ({ year, month }) => {
        setFilterYear(year);
        setFilterMonth(month);

        if (isMobile) {
            setMonthsPanelOpen(false);
        }
    };

    const normalizeNumber = (v, fallback = 10) => {
        if (Array.isArray(v)) v = v[0];
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : fallback;
    };

    const buildParams = (pageParam, perPageParam) => {
        const params = {
            page: normalizeNumber(pageParam, 1),
            per_page: 10,
            year: filterYear,
            month: filterMonth,
            day: filterDay || undefined,
            search_by: searchBy,
            search: searchText?.trim() || undefined,
        };

        Object.keys(params).forEach((k) => {
            if (params[k] === undefined || params[k] === null || params[k] === "") {
                delete params[k];
            }
        });

        return params;
    };

    const loadData = async (pageParam = page, perPageParam = perPage) => {
        setLoadingCollections(true);

        try {
            const params = buildParams(pageParam, perPageParam);
            const { data } = await getAllIncomeCollection(token, params);

            if (data?.success) {
                setMyCollections(data?.data ?? []);
                setMeta(
                    data?.meta ?? {
                        current_page: pageParam,
                        last_page: 1,
                        per_page: perPageParam,
                        total: (data?.data ?? []).length,
                    }
                );

                setPage(data?.meta?.current_page ?? pageParam);
                setPerPage(data?.meta?.per_page ?? perPageParam);
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setLoadingCollections(false);
        }
    };

    const applyFilters = () => {
        setPage(1);
        loadData(1, perPage);
    };

    return (
        <PageContainer maxWidth={false}>
            <Grid container spacing={2.5}>
                <Grid item xs={12}>
                    <FiltersCard elevation={0}>
                        <Box className="filters-content">
                            <Box className="filters-left">
                                <FormControl size="small" sx={{ minWidth: 110, ...fieldStyles }}>
                                    <InputLabel>Año</InputLabel>
                                    <Select
                                        label="Año"
                                        value={filterYear}
                                        onChange={(e) => setFilterYear(Number(e.target.value))}
                                    >
                                        {years.map((y) => (
                                            <MenuItem key={y} value={y}>
                                                {y}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" sx={{ minWidth: 140, ...fieldStyles }}>
                                    <InputLabel>Mes</InputLabel>
                                    <Select
                                        label="Mes"
                                        value={filterMonth}
                                        onChange={(e) => {
                                            setFilterMonth(Number(e.target.value));
                                            setFilterDay("");
                                        }}
                                    >
                                        {MONTHS.map((m) => (
                                            <MenuItem key={m.value} value={m.value}>
                                                {m.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl size="small" sx={{ minWidth: 110, ...fieldStyles }}>
                                    <InputLabel>Día</InputLabel>
                                    <Select
                                        label="Día"
                                        value={filterDay}
                                        onChange={(e) => setFilterDay(e.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em>Todos</em>
                                        </MenuItem>

                                        {daysInMonth.map((d) => (
                                            <MenuItem key={d} value={String(d)}>
                                                {d}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <TextField
                                    size="small"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    placeholder="Buscar por nombre..."
                                    sx={{ minWidth: { xs: "100%", md: 280 }, ...fieldStyles }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") applyFilters();
                                    }}
                                />
                            </Box>

                            <Box className="filters-actions">
                                {isMobile && (
                                    <Button
                                        variant="outlined"
                                        startIcon={<CalendarMonthOutlinedIcon />}
                                        onClick={() => setMonthsPanelOpen(true)}
                                        sx={{
                                            ...buttonStyles,
                                            color: "#10285f",
                                            borderColor: "rgba(214, 170, 58, 0.75)",
                                            background: "#fff",
                                            "&:hover": {
                                                borderColor: "#d6aa3a",
                                                background: "rgba(214, 170, 58, 0.12)",
                                            },
                                        }}
                                    >
                                        Ver meses
                                    </Button>
                                )}

                                <Button
                                    variant="contained"
                                    startIcon={<SearchIcon />}
                                    onClick={applyFilters}
                                    sx={{
                                        ...buttonStyles,
                                        background: "linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)",
                                        boxShadow: "0 6px 14px rgba(15, 34, 77, 0.18)",
                                        "&:hover": {
                                            background: "linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)",
                                        },
                                    }}
                                >
                                    Buscar
                                </Button>

                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => setAddGameCodes(true)}
                                    sx={{
                                        ...buttonStyles,
                                        background: "linear-gradient(90deg, #d6aa3a 0%, #f3cd61 100%)",
                                        color: "#10285f",
                                        boxShadow: "0 6px 14px rgba(214, 170, 58, 0.24)",
                                        "&:hover": {
                                            background: "linear-gradient(90deg, #f3cd61 0%, #d6aa3a 100%)",
                                        },
                                    }}
                                >
                                    Agregar
                                </Button>
                            </Box>
                        </Box>
                    </FiltersCard>
                </Grid>

                {!isMobile && (
                    <Grid item xs={12} md={3}>
                        <LeftPlaysPanel
                            startYear={2025}
                            onSelectMonth={handleSelectMonth}
                        />
                    </Grid>
                )}

                <Grid item xs={12} md={!isMobile ? 9 : 12}>
                    <CollectionsCard elevation={0}>
                        {loadingCollections ? (
                            <LoadingBox>
                                <CircularProgress sx={{ color: "#d6aa3a" }} />
                                <Typography>Cargando colecciones...</Typography>
                            </LoadingBox>
                        ) : (
                            <>
                                {myCollections.length > 0 ? (
                                    myCollections.map((item) => (
                                        <StyledAccordion
                                            expanded={expanded === item?.id}
                                            onChange={handleChange(item?.id)}
                                            key={item?.uuid}
                                            disableGutters
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls="panel1bh-content"
                                                id="panel1bh-header"
                                            >
                                                <Box className="accordion-summary">
                                                    <Box>
                                                        <Typography className="collection-title">
                                                            {item?.user_id != auth?.id && "(Externo) - "}
                                                            {item?.name}
                                                        </Typography>

                                                        <Typography className="collection-meta">
                                                            {item?.code_count} códigos · Valor x{" "}
                                                            {item?.code_amount ?? 0}
                                                        </Typography>
                                                    </Box>

                                                    <Box className="amount-pill">
                                                        {moneyFormat(item?.total_amount)}
                                                    </Box>
                                                </Box>
                                            </AccordionSummary>

                                            <AccordionDetails>
                                                {expanded === item?.id && (
                                                    <GameCodesTable
                                                        collectionUuid={item?.uuid}
                                                        nameDoc={item?.name}
                                                    />
                                                )}
                                            </AccordionDetails>
                                        </StyledAccordion>
                                    ))
                                ) : (
                                    <EmptyState>
                                        <i className="fa-regular fa-folder-open"></i>
                                        <Typography className="empty-title">
                                            No hay colecciones
                                        </Typography>
                                        <Typography className="empty-text">
                                            Crea una colección nueva o cambia los filtros seleccionados.
                                        </Typography>
                                    </EmptyState>
                                )}

                                {meta?.last_page > 1 && (
                                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                                        <Pagination
                                            count={meta?.last_page ?? 1}
                                            page={meta?.current_page ?? page}
                                            onChange={(_, newPage) => {
                                                setPage(newPage);
                                                loadData(newPage, perPage);
                                            }}
                                            color="primary"
                                            shape="rounded"
                                        />
                                    </Box>
                                )}

                                <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Mostrando {meta?.from ?? 0} - {meta?.to ?? 0} de{" "}
                                        {meta?.total ?? 0}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </CollectionsCard>
                </Grid>
            </Grid>

            {isMobile && (
                <Drawer
                    anchor="right"
                    open={monthsPanelOpen}
                    onClose={() => setMonthsPanelOpen(false)}
                >
                    <MonthsDrawerContent>
                        <Box className="drawer-header">
                            <Typography className="drawer-title">
                                Filtrar por mes
                            </Typography>

                            <IconButton
                                size="small"
                                onClick={() => setMonthsPanelOpen(false)}
                                sx={{
                                    color: "#10285f",
                                    background: "rgba(16,40,95,0.06)",
                                    "&:hover": {
                                        background: "rgba(214,170,58,0.16)",
                                    },
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <LeftPlaysPanel
                            startYear={2025}
                            onSelectMonth={handleSelectMonth}
                        />
                    </MonthsDrawerContent>
                </Drawer>
            )}

            {addGameCodes && (
                <Modal open={addGameCodes} onClose={handleClose}>
                    <ModalCenter>
                        <ModalCard>
                            <ModalHeader>
                                <Typography className="title">Agrega una colección</Typography>

                                <IconButton
                                    onClick={handleClose}
                                    size="small"
                                    sx={{
                                        color: "#10285f",
                                        background: "rgba(16,40,95,0.06)",
                                        "&:hover": {
                                            background: "rgba(214,170,58,0.16)",
                                        },
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </ModalHeader>

                            <GameCodesForm
                                loadData={loadData}
                                onClose={() => setAddGameCodes(false)}
                            />
                        </ModalCard>
                    </ModalCenter>
                </Modal>
            )}
        </PageContainer>
    );
};

export default GameCodes;

const PageContainer = styled(Container)`
    padding: 1.4rem !important;
    background: #f5f7fb;
    min-height: 100%;

    @media (max-width: 600px) {
        padding: 1rem !important;
    }
`;

const FiltersCard = styled(Paper)`
    padding: 1rem;
    border-radius: 22px !important;
    border: 1px solid rgba(214, 170, 58, 0.26);
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%) !important;
    box-shadow: 0 12px 28px rgba(15, 34, 77, 0.08) !important;

    .filters-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        flex-wrap: wrap;
    }

    .filters-left,
    .filters-actions {
        display: flex;
        align-items: center;
        gap: 0.8rem;
        flex-wrap: wrap;
    }

    @media (max-width: 900px) {
        .filters-content {
            flex-direction: column;
            align-items: stretch;
        }

        .filters-left,
        .filters-actions {
            width: 100%;
        }

        .filters-left > .MuiFormControl-root,
        .filters-left > .MuiTextField-root {
            flex: 1 1 calc(50% - 0.8rem);
            min-width: calc(50% - 0.8rem) !important;
        }

        .filters-actions button {
            flex: 1;
        }
    }

    @media (max-width: 600px) {
        padding: 0.85rem;

        .filters-left,
        .filters-actions {
            flex-direction: column;
            align-items: stretch;
        }

        .filters-left > .MuiFormControl-root,
        .filters-left > .MuiTextField-root,
        .filters-actions button {
            width: 100%;
            min-width: 100% !important;
            flex: unset;
        }
    }
`;

const CollectionsCard = styled(Paper)`
    padding: 1rem;
    border-radius: 22px !important;
    border: 1px solid rgba(15, 34, 77, 0.1);
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%) !important;
    box-shadow: 0 12px 28px rgba(15, 34, 77, 0.08) !important;
    min-height: 420px;
`;

const StyledAccordion = styled(Accordion)`
    margin-bottom: 0.8rem !important;
    border-radius: 18px !important;
    overflow: hidden;
    border: 1px solid rgba(15, 34, 77, 0.1);
    background: #fff !important;
    box-shadow: none !important;

    &::before {
        display: none;
    }

    &:hover {
        border-color: rgba(214, 170, 58, 0.55);
        box-shadow: 0 8px 18px rgba(15, 34, 77, 0.07) !important;
    }

    .accordion-summary {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
    }

    .collection-title {
        font-weight: 900;
        color: #10285f;
        font-size: 1rem;
    }

    .collection-meta {
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.88rem;
        font-weight: 600;
        margin-top: 0.15rem;
    }

    .amount-pill {
        flex-shrink: 0;
        padding: 0.45rem 0.8rem;
        border-radius: 999px;
        background: rgba(214, 170, 58, 0.18);
        border: 1px solid rgba(214, 170, 58, 0.32);
        color: #10285f;
        font-weight: 900;
        font-size: 0.9rem;
    }

    @media (max-width: 600px) {
        .accordion-summary {
            align-items: flex-start;
            flex-direction: column;
        }

        .amount-pill {
            align-self: flex-start;
        }
    }
`;

const LoadingBox = styled.div`
    min-height: 320px;
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    align-items: center;
    justify-content: center;
    color: #10285f;
    font-weight: 700;
`;

const EmptyState = styled.div`
    min-height: 320px;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: #10285f;

    i {
        font-size: 2.4rem;
        color: #d6aa3a;
    }

    .empty-title {
        font-weight: 900;
        font-size: 1.25rem;
    }

    .empty-text {
        color: rgba(15, 34, 77, 0.62);
        max-width: 360px;
    }
`;

const MonthsDrawerContent = styled.div`
    width: 360px;
    max-width: 100vw;
    min-height: 100vh;
    padding: 1rem;
    box-sizing: border-box;
    background: #f5f7fb;

    .drawer-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 1rem;
    }

    .drawer-title {
        font-size: 1.1rem;
        font-weight: 900;
        color: #10285f;
    }
`;

const ModalCenter = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    width: 100vw;
    padding: 20px;
    box-sizing: border-box;
`;

const ModalCard = styled.div`
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    border-radius: 22px;
    width: 95%;
    max-width: 1200px;
    max-height: 95vh;
    overflow-y: auto;
    padding: 24px;
    border: 1px solid rgba(214, 170, 58, 0.28);
    box-shadow: 0 18px 48px rgba(0, 0, 0, 0.22);

    @media (max-width: 600px) {
        width: 100%;
        max-height: 92vh;
        padding: 16px;
        border-radius: 18px;
    }
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;

    .title {
        font-size: 1.5rem;
        font-weight: 900;
        color: #10285f;
    }
`;